/**
 * Pool Reports Offhire Owner Details Page
 *
 * Displays vessels for a specific owner with their offhire data.
 * Shows vessel-level breakdown of offhire fuel consumption and costs.
 * UI matches Angular implementation exactly.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ExternalLink } from 'lucide-react';
import { Button, Skeleton } from '@/components/ui';
import { useOffhireOwner } from '@/hooks/usePoolReports';
import { usePoolReportsStore } from '@/store';
import { CSVService } from '@/utils/csvExport';
import { formatNumber } from '@/utils/formatters';
import type { Offhire } from '@/types/poolReports.types';
import {
  POOL_REPORT_VESSEL_COL_HEADER,
  poolReportTheadMutedClass,
  poolReportTheadStickyClass,
  poolReportThClass,
  poolReportThGroupClass,
  poolReportThGroupSideClass,
} from '@/components/poolReports/poolReportTableChrome';

interface HiddenFuelTypes {
  HSFO: boolean;
  MOGO: boolean;
  LSFO: boolean;
  LSGO: boolean;
}

export default function PoolReportsOffhireOwnerPage() {
  const navigate = useNavigate();
  const { ownerId } = useParams<{ ownerId: string }>();
  const [searchParams] = useSearchParams();
  const { reportType, setSubTitle } = usePoolReportsStore();

  const fileId = Number(searchParams.get('fileId'));
  const poolId = Number(searchParams.get('poolId'));
  const ownerName = searchParams.get('ownerName') || 'Owner';
  const numericOwnerId = ownerId ? Number(ownerId) : 0;

  const { vessels: ownerVessels, isLoading: isOwnerVesselsLoading } = useOffhireOwner(
    fileId,
    numericOwnerId,
    Boolean(fileId && ownerId)
  );
  const offhires: Offhire[] = useMemo(() => ownerVessels || [], [ownerVessels]);
  const isLoading = isOwnerVesselsLoading;
  const [hiddenFuelTypes, setHiddenFuelTypes] = useState<HiddenFuelTypes>({
    HSFO: false,
    MOGO: false,
    LSFO: false,
    LSGO: false,
  });

  // Set subtitle for navigation
  useEffect(() => {
    setSubTitle({
      value: `Offhire Breakdown for: ${ownerName}`,
      backLink: `/reports/${reportType}/offhire`,
      queryParams: {
        poolId: poolId.toString(),
        fileId: fileId.toString(),
      },
    });

    return () => {
      setSubTitle(null);
    };
  }, [ownerName, reportType, poolId, fileId, setSubTitle]);

  // Calculate which fuel types to suppress (hide columns where all values are 0)
  useEffect(() => {
    if (offhires.length === 0) {
      return;
    }

    const totals = {
      HSFO: 0,
      MOGO: 0,
      LSFO: 0,
      LSGO: 0,
    };

    offhires.forEach((item) => {
      totals.HSFO += (item.Fuel || 0) + (item.FuelPrice || 0) + (item.FuelSum || 0);
      totals.MOGO += (item.Diesel || 0) + (item.DieselPrice || 0) + (item.DieselSum || 0);
      totals.LSFO += (item.LSFO || 0) + (item.LSFOPrice || 0) + (item.LSFOSum || 0);
      totals.LSGO += (item.LSGO || 0) + (item.LSGOPrice || 0) + (item.LSGOSum || 0);
    });

    setHiddenFuelTypes({
      HSFO: totals.HSFO === 0,
      MOGO: totals.MOGO === 0,
      LSFO: totals.LSFO === 0,
      LSGO: totals.LSGO === 0,
    });
  }, [offhires]);

  const handleExportCSV = () => {
    if (offhires.length === 0) return;

    const headers: { [key: string]: string } = {
      VesselName: '',
      Days: 'Days',
      Hours: 'Hours',
      Minutes: 'Minutes',
    };

    if (!hiddenFuelTypes.HSFO) {
      headers.Fuel = 'HSFO MT';
      headers.FuelPrice = 'HSFO Price $/MT';
      headers.FuelSum = 'HSFO $';
    }
    if (!hiddenFuelTypes.MOGO) {
      headers.Diesel = 'MO/GO MT';
      headers.DieselPrice = 'MO/GO Price $/MT';
      headers.DieselSum = 'MO/GO $';
    }
    if (!hiddenFuelTypes.LSFO) {
      headers.LSFO = 'LSFO MT';
      headers.LSFOPrice = 'LSFO Price $/MT';
      headers.LSFOSum = 'LSFO $';
    }
    if (!hiddenFuelTypes.LSGO) {
      headers.LSGO = 'LSGO MT';
      headers.LSGOPrice = 'LSGO Price $/MT';
      headers.LSGOSum = 'LSGO $';
    }

    headers.Lumpsum = 'Lumpsum';
    headers.Total = 'Total';

    const excludeFields = ['OwnerID', 'ReportDisplayOrder', 'VesselID', 'OwnerName'];
    const heading = Object.keys(headers).filter((key) => !excludeFields.includes(key));

    const reportRows = offhires.map((item: Offhire) =>
      heading.map((key) => {
        const value = item[key as keyof Offhire];
        if (value === null || value === undefined) return '';
        if (typeof value === 'number') {
          // Apply appropriate decimal places based on column
          if (key.includes('Price')) return formatNumber(value, 2, 2);
          if (
            key.includes('Fuel') ||
            key.includes('Diesel') ||
            key.includes('LSFO') ||
            key.includes('LSGO')
          ) {
            return formatNumber(value, 1, 1);
          }
          return formatNumber(value, 0, 0);
        }
        return String(value);
      })
    );

    const fileName = `offhire-report-vessels-${ownerName}`;
    CSVService.convert(
      reportRows,
      heading.map((key) => headers[key] || key)
    ).download(fileName);
  };

  const handleVesselClick = (offhire: Offhire) => {
    if (!offhire.VesselID) return; // Don't navigate for total row

    const route = `/reports/${reportType}/offhire/owner/${ownerId}/${offhire.VesselID}`;
    navigate(
      `${route}?poolId=${poolId}&fileId=${fileId}&ownerName=${encodeURIComponent(ownerName)}&vesselName=${encodeURIComponent(offhire.VesselName || '')}`
    );
  };

  const isTotal = (offhire: Offhire) => !offhire.VesselID;

  return (
    <div className="space-y-4">
      {/* Table - Angular Style with Multi-Row Header */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800">
        {/* Table Header with Export Button */}
        <div className="flex items-center justify-end border-b border-gray-200 px-6 py-4 dark:border-dark-700">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCSV}
            leftIcon={<Download className="h-4 w-4" />}
            disabled={isLoading || offhires.length === 0}
            className="rounded-full px-4 shadow-sm"
          >
            Export to Excel
          </Button>
        </div>

        <div className="report-table-shell">
          <table className="report-table">
            {isLoading ? (
              <>
                <thead className={poolReportTheadMutedClass}>
                  <tr>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <th key={i} className={poolReportThClass}>
                        <Skeleton className="ml-auto h-4 w-20" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                  {Array.from({ length: 5 }).map((_, rowIdx) => (
                    <tr key={rowIdx}>
                      {Array.from({ length: 12 }).map((_, colIdx) => (
                        <td key={colIdx} className="px-2.5 py-2 sm:px-3 sm:py-2.5">
                          <Skeleton className="h-10 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </>
            ) : (
              <>
                <thead className={poolReportTheadStickyClass}>
                  <tr>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      {POOL_REPORT_VESSEL_COL_HEADER}
                    </th>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      DAYS
                    </th>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      HOURS
                    </th>
                    <th
                      rowSpan={2}
                      className={clsx(
                        poolReportThGroupSideClass,
                        'border-r border-gray-200 dark:border-dark-700'
                      )}
                    >
                      MINUTES
                    </th>

                    {!hiddenFuelTypes.HSFO && (
                      <th
                        colSpan={3}
                        className={clsx(
                          poolReportThGroupClass,
                          'border-l border-r border-gray-200 dark:border-dark-700'
                        )}
                      >
                        HSFO
                      </th>
                    )}

                    {!hiddenFuelTypes.MOGO && (
                      <th
                        colSpan={3}
                        className={clsx(
                          poolReportThGroupClass,
                          'border-l border-r border-gray-200 dark:border-dark-700'
                        )}
                      >
                        MO/GO
                      </th>
                    )}

                    {!hiddenFuelTypes.LSFO && (
                      <th
                        colSpan={3}
                        className={clsx(
                          poolReportThGroupClass,
                          'border-l border-r border-gray-200 dark:border-dark-700'
                        )}
                      >
                        LSFO
                      </th>
                    )}

                    {!hiddenFuelTypes.LSGO && (
                      <th
                        colSpan={3}
                        className={clsx(
                          poolReportThGroupClass,
                          'border-l border-r border-gray-200 dark:border-dark-700'
                        )}
                      >
                        LSGO
                      </th>
                    )}

                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      LUMPSUM
                    </th>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      TOTAL
                    </th>
                  </tr>
                  <tr>
                    {!hiddenFuelTypes.HSFO && (
                      <>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-l border-gray-200 dark:border-dark-700'
                          )}
                        >
                          MT
                        </th>
                        <th className={poolReportThGroupSideClass}>$ / MT</th>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-r border-gray-200 dark:border-dark-700'
                          )}
                        >
                          $
                        </th>
                      </>
                    )}

                    {!hiddenFuelTypes.MOGO && (
                      <>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-l border-gray-200 dark:border-dark-700'
                          )}
                        >
                          MT
                        </th>
                        <th className={poolReportThGroupSideClass}>$ / MT</th>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-r border-gray-200 dark:border-dark-700'
                          )}
                        >
                          $
                        </th>
                      </>
                    )}

                    {!hiddenFuelTypes.LSFO && (
                      <>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-l border-gray-200 dark:border-dark-700'
                          )}
                        >
                          MT
                        </th>
                        <th className={poolReportThGroupSideClass}>$ / MT</th>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-r border-gray-200 dark:border-dark-700'
                          )}
                        >
                          $
                        </th>
                      </>
                    )}

                    {!hiddenFuelTypes.LSGO && (
                      <>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-l border-gray-200 dark:border-dark-700'
                          )}
                        >
                          MT
                        </th>
                        <th className={poolReportThGroupSideClass}>$ / MT</th>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-r border-gray-200 dark:border-dark-700'
                          )}
                        >
                          $
                        </th>
                      </>
                    )}
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                  {offhires?.map((offhire, idx) => {
                    const isTotalRow = isTotal(offhire);

                    return (
                      <tr
                        key={idx}
                        className={isTotalRow ? '' : 'hover:bg-gray-50 dark:hover:bg-dark-800'}
                      >
                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {isTotalRow ? (
                            <span className="font-semibold">{offhire.VesselName}</span>
                          ) : (
                            <div
                              className="flex cursor-pointer items-center justify-end gap-2"
                              onClick={() => handleVesselClick(offhire)}
                            >
                              <span className="font-semibold uppercase underline hover:no-underline">
                                {offhire.VesselName}
                              </span>
                              <ExternalLink className="size-[1.125rem] shrink-0 text-gray-500 dark:text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {offhire.Days}
                        </td>
                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {offhire.Hours}
                        </td>
                        <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                          {offhire.Minutes}
                        </td>

                        {/* HSFO Columns */}
                        {!hiddenFuelTypes.HSFO && (
                          <>
                            <td className="border-l border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.Fuel, 1, 1)}
                            </td>
                            <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.FuelPrice, 2, 2)}
                            </td>
                            <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.FuelSum, 0, 0)}
                            </td>
                          </>
                        )}

                        {/* MO/GO Columns */}
                        {!hiddenFuelTypes.MOGO && (
                          <>
                            <td className="border-l border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.Diesel, 1, 1)}
                            </td>
                            <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.DieselPrice, 2, 2)}
                            </td>
                            <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.DieselSum, 0, 0)}
                            </td>
                          </>
                        )}

                        {/* LSFO Columns */}
                        {!hiddenFuelTypes.LSFO && (
                          <>
                            <td className="border-l border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSFO, 1, 1)}
                            </td>
                            <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSFOPrice, 2, 2)}
                            </td>
                            <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSFOSum, 0, 0)}
                            </td>
                          </>
                        )}

                        {/* LSGO Columns */}
                        {!hiddenFuelTypes.LSGO && (
                          <>
                            <td className="border-l border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSGO, 1, 1)}
                            </td>
                            <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSGOPrice, 2, 2)}
                            </td>
                            <td className="border-r border-gray-200 px-2.5 py-2 text-right text-gray-900 dark:border-dark-700 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(offhire.LSGOSum, 0, 0)}
                            </td>
                          </>
                        )}

                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(offhire.Lumpsum, 0, 0)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(offhire.Total, 0, 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </>
            )}
          </table>
        </div>
      </div>

      {/* No Data State */}
      {!isLoading && offhires.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No offhire data available</p>
        </div>
      )}
    </div>
  );
}
